import { useState } from "react"
import { AddressEntity } from "../../Objects/Person/AddressEntity"
import { normalizeZipCode } from "../../utils/documentFormat"

type GoogleAddressComponent = {
  long_name: string
  short_name: string
  types: string[]
}

export const useCreateAddress = () => {
  const [id, setId] = useState(0)

  const [label, setLabel] = useState("")

  const [city, setCity] = useState("")

  const [street, setStreet] = useState("")

  const [state, setState] = useState("AC")

  const [neighBornHood, setNeighBornHood] = useState("")

  const [number, setNumber] = useState("")

  const [cep, setCep] = useState("")

  const [complement, setSetComplement] = useState("")

  const [latitude, setLatitude] = useState<number | null>(null)

  const [longitude, setLongitude] = useState<number | null>(null)

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)
  const handleStreetChange = (e: React.ChangeEvent<HTMLInputElement>) => setStreet(e.target.value)
  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => setState(e.target.value)
  const handleNeighBornHoodChange = (e: React.ChangeEvent<HTMLInputElement>) => setNeighBornHood(e.target.value)
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => setNumber(e.target.value)
  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => setCep(e.target.value)
  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)
  const handleComplementChange = (e: React.ChangeEvent<HTMLInputElement>) => setSetComplement(e.target.value)

  const findAddressComponent = (components: GoogleAddressComponent[], type: string, useShortName = false) => {
    const component = components.find((item) => item.types.includes(type))

    return useShortName ? component?.short_name ?? "" : component?.long_name ?? ""
  }

  const handleApplyGoogleAddress = (data: {
    addressComponents: GoogleAddressComponent[]
    latitude?: number
    longitude?: number
  }) => {
    const components = data.addressComponents

    const route = findAddressComponent(components, "route")
    const streetNumber = findAddressComponent(components, "street_number")
    const neighborhoodValue =
      findAddressComponent(components, "sublocality") || findAddressComponent(components, "neighborhood")
    const cityValue =
      findAddressComponent(components, "administrative_area_level_2") || findAddressComponent(components, "locality")
    const stateValue = findAddressComponent(components, "administrative_area_level_1", true)
    const zipCodeValue = findAddressComponent(components, "postal_code")

    if (route) {
      setStreet(route)
    }

    if (streetNumber) {
      setNumber(streetNumber)
    }

    if (neighborhoodValue) {
      setNeighBornHood(neighborhoodValue)
    }

    if (cityValue) {
      setCity(cityValue)
    }

    if (stateValue) {
      setState(stateValue)
    }

    if (zipCodeValue) {
      setCep(normalizeZipCode(zipCodeValue))
    }

    setLatitude(data.latitude ?? null)
    setLongitude(data.longitude ?? null)
  }

  const handleSetInitialValue = (address: AddressEntity | null | undefined) => {
    if (address) {
      setCity(address.city)
      setCep(address.zipCode)
      setNeighBornHood(address.neighborhood)
      setNumber(address.number)
      setStreet(address.street)
      setSetComplement(address.complement)
      setState(address.state)
      setId(address.id)
      setLabel(address.label)
      setLatitude(address.latitude ?? null)
      setLongitude(address.longitude ?? null)
    }
  }

  const handleSubmitForm = () => {
    if (!label || label.trim().length === 0) {
      return null
    }

    return new AddressEntity({
      id: id,
      city: city,
      state: state,
      street: street,
      neighborhood: neighBornHood,
      zipCode: cep,
      number: number,
      label: label,
      complement: complement,
      latitude: latitude ?? undefined,
      longitude: longitude ?? undefined,
    })
  }

  return {
    city,
    street,
    state,
    neighBornHood,
    number,
    complement,
    cep,
    label,
    latitude,
    longitude,
    handleCityChange,
    handleStreetChange,
    handleNeighBornHoodChange,
    handleStateChange,
    handleNumberChange,
    handleCepChange,
    handleComplementChange,
    handleSubmitForm,
    handleSetInitialValue,
    handleLabelChange,
    handleApplyGoogleAddress,
  }
}
